import { useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';

/**
 * Forced password change — shown after first login when the user account
 * was bootstrapped with a temporary password. Also accessible voluntarily
 * from Settings.
 */
export function ChangePassword() {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  async function submit(e: Event) {
    e.preventDefault();
    setError(null);
    if (newPw.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.ok) {
        setError(body?.error || `Failed (${res.status})`);
        return;
      }
      const next = new URLSearchParams(window.location.search).get('next');
      setLocation(next && next.startsWith('/') ? next : '/mission');
    } catch (err: any) {
      setError(err?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="min-h-screen min-h-[100dvh] flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-text)] px-4">
      <div class="w-full max-w-sm">
        <div class="mb-8 text-center">
          <div class="text-2xl font-semibold mb-1">Change Password</div>
          <div class="text-sm text-[var(--color-text-muted)]">
            Set a new password before continuing.
          </div>
        </div>
        <form
          onSubmit={submit}
          class="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5 space-y-4"
        >
          <div>
            <label class="block text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">Current password</label>
            <input
              type="password"
              required
              autocomplete="current-password"
              value={currentPw}
              onInput={(e) => setCurrentPw((e.target as HTMLInputElement).value)}
              class="w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded px-3 py-2 text-[13px] outline-none focus:border-[var(--color-accent)]"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="block text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">New password (8+ chars)</label>
            <input
              type="password"
              required
              minLength={8}
              autocomplete="new-password"
              value={newPw}
              onInput={(e) => setNewPw((e.target as HTMLInputElement).value)}
              class="w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded px-3 py-2 text-[13px] outline-none focus:border-[var(--color-accent)]"
              disabled={submitting}
            />
          </div>
          <div>
            <label class="block text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              autocomplete="new-password"
              value={confirm}
              onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
              class="w-full bg-[var(--color-elevated)] border border-[var(--color-border)] rounded px-3 py-2 text-[13px] outline-none focus:border-[var(--color-accent)]"
              disabled={submitting}
            />
          </div>
          {error && (
            <div class="text-[12px] text-[var(--color-status-failed)] bg-[var(--color-status-failed)]/10 border border-[var(--color-status-failed)]/30 rounded px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !currentPw || newPw.length < 8 || newPw !== confirm}
            class="w-full bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded px-3 py-2 text-[13px] font-medium transition-colors"
          >
            {submitting ? 'Updating…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}
